import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { Icons } from "assets";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Filter from "shared/components/filterButton";
import Loader from "shared/components/loader/animation";
import Pagination from "shared/components/pagination";
import { toastMessage } from "shared/components/toast";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import useDebounce from "shared/customHooks/useDebounce";
import useWindowSize from "shared/customHooks/windowSize";
import Confirmation from "shared/modals/confirmation";
import ViewDemoRequest from "shared/modals/viewDemoRequest";
import EditDemoRequest from "shared/modals/editDemoRequest";
import { routeConstant } from "shared/routes/routesConstant";
import {
  getDemoRequestsService,
  reviewDemoRequestService,
} from "shared/services/adminService";
import SkeletonLoader from "./skeletonLoader";
import styles from "./style.module.scss";
import moment from "moment";

enum DemoRequestStatus {
  New = 0,
  Pending = 1,
  Approved = 2,
  Rejected = 3,
  Expired = 4,
  Active = 5,
  Inactive = 6,
}

const statusConfig: Record<number, { label: string; color: string }> = {
  [DemoRequestStatus.New]: { label: "New", color: "#2196f3" },
  [DemoRequestStatus.Pending]: { label: "Pending", color: "#ff9800" },
  [DemoRequestStatus.Approved]: { label: "Approved", color: "#4caf50" },
  [DemoRequestStatus.Rejected]: { label: "Rejected", color: "#f44336" },
  [DemoRequestStatus.Expired]: { label: "Expired", color: "#9e9e9e" },
  [DemoRequestStatus.Active]: { label: "Active", color: "#4caf50" },
  [DemoRequestStatus.Inactive]: { label: "Inactive", color: "#9e9e9e" },
};

function DemoRequests() {
  const navigate = useNavigate();
  const screenWidth = useWindowSize();
  const [activePage, setActivePage] = useState<number>(1);
  const [allAccounts, setAllAccounts] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [openMember, setOpenMember] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<any>(null);
  const [total, setTotal] = useState<any>(null);
  const [openConfirm, setOpenConfirmation] = useState<boolean>(false);
  const [actionLoader, setActionLoader] = useState<boolean>(false);
  const [action, setAction] = useState<any>({ label: "", type: "" });

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      // Nothing changed once trimmed (e.g. only spaces were typed) — keep the
      // current page and skip the request entirely.
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800,
  );

  useBreadcrumbs({
    title: "Demo Requests",
    path: routeConstant.demoRequests.path,
  });

  const getAllAccounts = (bool: boolean) => {
    if (bool) {
      setLoader(true);
    }
    getDemoRequestsService(
      searchText,
      selectedFilter?.value ?? null,
      activePage,
    )
      .then(({ data }) => {
        setTotal(data?.data?.total);
        setTotalPage(data?.data?.last_page);
        setAllAccounts(data?.data?.data);
        setLoader(false);
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setLoader(false);
      });
  };

  useEffect(() => {
    getAllAccounts(true);
    // eslint-disable-next-line
  }, [searchText, activePage, selectedFilter]);

  const filterOption = [
    { option: "New", value: 0 },
    { option: "Pending", value: 1 },
    { option: "Approved", value: 2 },
    { option: "Rejected", value: 3 },
    { option: "Expired", value: 4 },
    { option: "Active", value: 5 },
    { option: "Inactive", value: 6 },
  ];

  const resetFilters = () => {
    setSelectedFilter(null);
    setSearch("");
    setSearchText("");
    setActivePage(1);
  };

  const handleOpenConfirmation = (label: string, type: string, item: any) => {
    setOpenConfirmation(true);
    setAction({ label, type });
    setSelectedItem(item);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
    setAction({ label: "", type: "" });
    setSelectedItem(null);
  };

  const handleToggleActive = () => {
    const newStatus =
      selectedItem?.status === DemoRequestStatus.Active
        ? DemoRequestStatus.Inactive
        : DemoRequestStatus.Active;
    const formData = new FormData();
    formData.append("status", String(newStatus));
    setActionLoader(true);
    reviewDemoRequestService(selectedItem?.id, formData)
      .then(({ data }) => {
        toastMessage("success", data?.message);
        setActionLoader(false);
        getAllAccounts(false);
        handleCloseConfirmation();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.message);
        setActionLoader(false);
        handleCloseConfirmation();
      });
  };

  return (
    <div>
      <Box className={styles.topLevelContainer}>
        <Box className={styles.topHeadContainer}>
          <Box>
            <div className={styles.tableHeading}>Demo Requests</div>
            <div className={styles.tableSubHeading}>
              Manage demo requests from here.
            </div>
          </Box>
          <Box className={styles.filterContainer}>
            {(searchText?.length > 0 || selectedFilter) && (
              <div style={{ marginRight: "10px" }}>
                <button
                  className={styles.resetBtn}
                  onClick={() => resetFilters()}
                >
                  Reset Filters
                </button>
              </div>
            )}

            <div className={styles.userFilter}>
              <Filter
                label="Search by Status"
                filterOption={filterOption}
                filterStyles={styles.filterStyles}
                selectedOption={selectedFilter}
                setSelectedOption={setSelectedFilter}
                setActivePage={setActivePage}
              />
            </div>
            <div className={styles.gridContainer}>
              <Icons.SearchIcon className={styles.searchIcon} />
              <input
                placeholder="Search"
                className={styles.input}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </Box>
        </Box>
        <Box className={styles.counterContainer}>Total Results: {total}</Box>

        <TableContainer
          component={Paper}
          className={styles.table}
          sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
        >
          <Table aria-label="simple table">
            <TableHead>
              <TableRow className={styles.tableHead}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>School Name</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loader ? (
                <SkeletonLoader />
              ) : (
                allAccounts?.map((item: any) => {
                  const status = statusConfig[item?.status];
                  return (
                    <TableRow key={item?.id} className={styles.tableRow}>
                      <TableCell>
                        {item?.first_name} {item?.last_name}
                      </TableCell>
                      <TableCell className={styles.emailCell}>
                        <Tooltip title={item?.email || "Not Provided"} arrow>
                          <span>{item?.email || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{item?.school_name || "—"}</TableCell>
                      <TableCell>
                        {moment(item?.created_at).format("MMM DD, YYYY")}
                      </TableCell>
                      <TableCell>
                        {status ? (
                          <span
                            className={styles.statusChip}
                            style={{ backgroundColor: status.color }}
                          >
                            {status.label}
                          </span>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>
                        <Box
                          display={"flex"}
                          alignItems={"center"}
                          className={styles.actionBtn}
                        >
                          <Tooltip title={"View"} arrow>
                            <button
                              onClick={() => {
                                // Active and Expired both already have a school
                                // account behind them, so the eye opens the
                                // school detail. Anything else still has no
                                // account yet and goes to plan creation.
                                const hasSchoolAccount =
                                  (item?.status === DemoRequestStatus.Active ||
                                    item?.status ===
                                      DemoRequestStatus.Expired) &&
                                  item?.created_user_id;

                                navigate(
                                  hasSchoolAccount
                                    ? routeConstant.demoRequestSchoolDetail.path.replace(
                                        ":id",
                                        item?.created_user_id,
                                      )
                                    : routeConstant.demoRequestCreatePlan.path.replace(
                                        ":id",
                                        item?.id,
                                      ),
                                );
                              }}
                            >
                              <RemoveRedEyeIcon />
                            </button>
                          </Tooltip>
                          {(item?.status === DemoRequestStatus.Active ||
                            item?.status === DemoRequestStatus.Inactive) && (
                            <Tooltip
                              title={
                                item?.status === DemoRequestStatus.Active
                                  ? "Deactivate"
                                  : "Activate"
                              }
                              arrow
                            >
                              <button
                                onClick={() =>
                                  handleOpenConfirmation(
                                    `Are you sure you want to ${
                                      item?.status === DemoRequestStatus.Active
                                        ? "deactivate"
                                        : "activate"
                                    } this demo request?`,
                                    item?.status === DemoRequestStatus.Active
                                      ? "inactive"
                                      : "active",
                                    item,
                                  )
                                }
                              >
                                {item?.status === DemoRequestStatus.Active ? (
                                  <FlashOnIcon />
                                ) : (
                                  <FlashOffIcon />
                                )}
                              </button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {allAccounts?.length === 0 && !loader && (
          <div className={styles.noUserCard}>
            <Icons.NoUsersData />
            <h5>No Data Found</h5>
          </div>
        )}

        <ViewDemoRequest
          open={openMember}
          handleClose={() => {
            setSelectedItem(null);
            setOpenMember(false);
          }}
          selectedItem={selectedItem}
        />

        <EditDemoRequest
          open={openEdit}
          handleClose={() => {
            setSelectedItem(null);
            setOpenEdit(false);
          }}
          selectedItem={selectedItem}
          onSuccess={() => getAllAccounts(false)}
        />

        <Confirmation
          open={openConfirm}
          handleClose={handleCloseConfirmation}
          handleClick={handleToggleActive}
          text={action.label}
        />

        {actionLoader && <Loader />}

        {totalPage > 1 && (
          <Pagination
            totalPages={totalPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        )}
      </Box>
    </div>
  );
}

export default DemoRequests;
